/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { memo } from "react";
import { TableCell } from "../../components/ui/table";
import { theme } from "../../styles/theme/theme";
import { formatCurrencyOrNull, formatWOS } from "../../utils/format";
import { MEASURE_FORMAT } from "../../lib/supplyWalkMeasures";
import { MEASURE, maskWOSForEmptyDemand } from "./calculations";
import { PortalTooltip } from "./PortalTooltip";
import { STATUS_CONFIG, wosCellBg } from "./constants";
import { s } from "./RetailerSupplyView.styles";
import type { RowStatus, SkuRow as SkuRowType } from "./types";
import { formatGrainValue, sameRounded } from "./utils";

const dashCellStyle = css`
  ${s.skuValueCell};
  color: ${theme.colors.mutedForeground};
`;

export interface SkuRowProps {
  row: SkuRowType;
  isExpanded: boolean;
  toggle: () => void;
  aggKey: string;
  /** Slice of editedMeasures for this row (undefined when row is untouched). */
  editsForRow: Record<string, number[]> | undefined;
  /** Slice of clearedCells for this row (undefined when row has no clears). */
  clearsForRow: Set<number> | undefined;
  groupStatus: RowStatus;
  visiblePeriodSet: Set<string>;
  periodColumns: string[];
  pastPeriodSet: Set<string>;
  /** This PL is the only one for its SKU, rendered directly (no SKU-header
   *  row above). Drops the left-edge accent and renders the SKU as the row's
   *  primary label instead of the muted-italic "inherited context" treatment. */
  standalone?: boolean;
}

function SkuRowImpl({
  row,
  isExpanded,
  toggle,
  aggKey,
  editsForRow,
  clearsForRow,
  groupStatus,
  visiblePeriodSet,
  periodColumns,
  pastPeriodSet,
  standalone = false,
}: SkuRowProps) {
  const effectiveMeasures = editsForRow ?? row.measures;
  const isAggDelta = aggKey === MEASURE.FORECAST_SHIPMENTS_DELTA;
  const isAggWOS = aggKey === "ending_days_of_supply";
  const isAggActiveSales = aggKey === "actual_sales" || aggKey === "forecast_sales";
  const isAggShipmentMeasure =
    aggKey === "simulated_shipments_placed_atomic" ||
    aggKey === "simulated_shipments_placed_planner_override";
  // Mirror SkuDetailRow's past-period fold: for the two forecast-shipment rows,
  // past cells display historical open_shipments_placed instead of the always-
  // zero engine forecast so the aggregate row agrees with the expanded detail.
  const rawAggValues = effectiveMeasures[aggKey] ?? effectiveMeasures.ending_inventory ?? [];
  const aggValues = isAggShipmentMeasure
    ? rawAggValues.map((v, i) =>
        pastPeriodSet.has(periodColumns[i])
          ? (row.measures.open_shipments_placed?.[i] ?? 0)
          : v,
      )
    : isAggWOS
      ? maskWOSForEmptyDemand(rawAggValues, effectiveMeasures.forecast_sales ?? [])
      : rawAggValues;
  const aggTotal = aggValues.reduce((a, v) => a + v, 0);
  // CHO-459: forecast_revenue renders as currency; other measures keep their
  // existing grain-aware integer formatting.
  const formatAgg = isAggWOS
    ? formatWOS
    : MEASURE_FORMAT[aggKey] === "currency"
      ? (v: number) => formatCurrencyOrNull(v)
      : formatGrainValue;
  const atomicVals = row.measures.simulated_shipments_placed_atomic ?? [];
  const overrideVals = effectiveMeasures.simulated_shipments_placed_planner_override ?? [];
  const skuCellDiff = (ci: number): boolean => {
    if (!isAggShipmentMeasure) return false;
    if (clearsForRow?.has(ci)) return false;
    if (atomicVals[ci] === undefined) return false;
    return !sameRounded(atomicVals[ci], overrideVals[ci]);
  };
  const statusCfg = STATUS_CONFIG[groupStatus];

  return (
    <>
      {/* Left-edge accent visually nests this PL row under the SKU header
          above. The 3px inset shadow stays inside the cell's bounding box,
          so the sticky background continues to obscure the scrollable
          period columns when the user scrolls horizontally. Standalone rows
          (single-PL SKUs with no header above) skip the accent. */}
      <TableCell
        css={
          standalone
            ? s.skuExpandCell
            : [s.skuExpandCell, css`box-shadow: inset 3px 0 0 ${theme.colors.gray300};`]
        }
        onClick={toggle}
      >
        {isExpanded ? <ChevronDown css={s.skuChevron} /> : <ChevronRight css={s.skuChevron} />}
      </TableCell>

      <TableCell css={s.statusCell} onClick={toggle}>
        <PortalTooltip text={statusCfg.label}>
          <span css={s.statusDotSmall(statusCfg.color)} />
        </PortalTooltip>
      </TableCell>

      {/* Nested rows repeat the SKU in muted italic so it reads as inherited
          context — the SKU header above carries it as the bold group title.
          Standalone rows render the SKU as the row's primary label instead. */}
      <TableCell css={s.skuNameCell(isExpanded)} onClick={toggle}>
        {standalone ? (
          row.sku
        ) : (
          <span
            css={css`
              color: ${theme.colors.mutedForeground};
              font-style: italic;
              font-weight: 400;
              padding-left: 0.75rem;
            `}
          >
            {row.sku}
          </span>
        )}
      </TableCell>

      {/* Standalone (single-PL) rows show a "1" count badge when collapsed —
          uniform with the multi-PL header badge instead of a lone text name —
          and reveal the planning-level name on expand. One click swaps the
          badge for the name AND opens the measure walk below; no intermediate
          step. Nested PL rows always show the name (they disambiguate siblings). */}
      <TableCell css={s.skuPlanningLevelCell(isExpanded)} onClick={toggle} title={standalone ? row.planningLevel : undefined}>
        {standalone && !isExpanded ? (
          <span css={s.skuHeaderPlCountBadge}>1</span>
        ) : (
          row.planningLevel
        )}
      </TableCell>

      {aggValues.map((v, ci) => {
        if (!visiblePeriodSet.has(periodColumns[ci])) return null;
        const isPast = pastPeriodSet.has(periodColumns[ci]);
        let bg = isPast ? theme.colors.pastPeriodBg : "";
        let color = theme.colors.foreground;
        if (isAggWOS) bg = wosCellBg(v, row.targetWOS) || bg;
        if (aggKey === "simulated_shipments_received" && v > 0 && !isPast) bg = theme.colors.green200;
        if (
          isAggActiveSales &&
          ((aggKey === "actual_sales" && isPast) || (aggKey === "forecast_sales" && !isPast))
        ) {
          color = theme.colors.red600;
        }
        if (isAggDelta) {
          color = v > 0 ? theme.colors.status.success : v < 0 ? theme.colors.status.error : theme.colors.foreground;
        }
        if (!isPast && skuCellDiff(ci)) bg = theme.colors.gray200;
        if (aggKey === "actual_sales" && !isPast) {
          return (
            <TableCell key={ci} onClick={toggle} css={dashCellStyle}>
              —
            </TableCell>
          );
        }
        return (
          <TableCell
            key={ci}
            onClick={toggle}
            css={css`
              ${s.skuValueCell};
              color: ${color};
              background-color: ${bg || "transparent"};
            `}
          >
            {formatAgg(v)}
          </TableCell>
        );
      })}

      <TableCell
        css={
          isAggDelta
            ? css`
                ${s.skuTotalCell};
                color: ${aggTotal > 0 ? theme.colors.status.success : aggTotal < 0 ? theme.colors.status.error : theme.colors.foreground};
              `
            : s.skuTotalCell
        }
        onClick={toggle}
      >
        {isAggWOS
          ? formatWOS(aggTotal / Math.max(1, aggValues.length))
          : formatAgg(aggTotal)}
      </TableCell>
    </>
  );
}

// Custom equality: `toggle` identity churns from DataTable's per-call binding,
// but its closure captures only the (stable) rowId, so a stale toggle is still
// correct. Ignoring it lets unchanged rows skip render.
export const SkuRow = memo(
  SkuRowImpl,
  (prev, next) =>
    prev.row === next.row &&
    prev.isExpanded === next.isExpanded &&
    prev.aggKey === next.aggKey &&
    prev.editsForRow === next.editsForRow &&
    prev.clearsForRow === next.clearsForRow &&
    prev.groupStatus === next.groupStatus &&
    prev.visiblePeriodSet === next.visiblePeriodSet &&
    prev.periodColumns === next.periodColumns &&
    prev.pastPeriodSet === next.pastPeriodSet &&
    prev.standalone === next.standalone,
);
